import http, { ApiRequestConfig } from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import type {
  IApprovalConfigurationAvailability,
  ICustomerIdentityPropagationScope,
} from "@models/approval/approvalModel"
import {
  ApprovalConfigurationModel,
  customerUpdateRequestModel,
} from "@models/approval/approvalModel"

class ApprovalService {
  // CONFIG
  public async CreateOrUpdatePosition(body: any) {
    const res = await http.post(
      "api/services/app/Approval/BulkCreateOrUpdatePosition",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async getConfig(id: any): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get(
      "api/services/app/Approval/GetRequestConfigurationDetail",
      {
        params: { id },
      }
    )
    const { result } = res.data
    return ApprovalConfigurationModel.assign(result)
  }

  public async bulkDelete(ids: any[]) {
    const res = await http.post("api/services/app/Approval/BulkDeleteFlow", ids)
    return res.data
  }

  public async getAllConfig(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/Approval/GetAllRequestConfiguration",
      { params }
    )
    return res.data.result
  }
  public async getRejectReason(params: any) {
    const res = await http.get("api/services/app/Approval/GetRejectReason", {
      params,
    })
    return res.data.result
  }
  // APPROVAL FLOW

  /**
   * `options.silent`: caller tự báo kết quả nên KHÔNG bật popup thành công/lỗi của lượt này.
   * Dùng cho thao tác duyệt hàng loạt — mỗi phiếu một lời gọi, để mặc định thì người dùng nhận
   * đúng N popup và popup cuối che mất phiếu nào đã lỗi.
   */
  public async updateRequestStatus(
    body: any,
    timeout?: number,
    options?: { silent?: boolean }
  ) {
    const res = await http.put(
      "api/services/app/Approval/UpdateRequestStatus",
      body,
      {
        timeout: timeout || 120000, // Default 120 seconds for UpdateRequestStatus
        skipErrorNotification: options?.silent,
      } as ApiRequestConfig
    )
    if (!options?.silent) {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("APPROVAL_SUCCESSFULLY")
      )
    }
    return res.data
  }

  // FOR CUSTOMER

  public async getAllCustomerUpdateRequest(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/Approval/GetAllCustomerUpdateRequest",
      { params }
    )
    return res.data.result
  }
  public async getCustomerUpdateRequest(id: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/Approval/GetCustomerUpdateRequest",
      { params: { id } }
    )
    return customerUpdateRequestModel.assign(res.data.result)
  }

  /**
   * SKLCRMF01-447 — phạm vi lan toả định danh KHHH (số hợp đồng / pháp nhân sẽ bị cập nhật).
   * Dùng đúng truy vấn của đường ghi ở BE nên con số không thể lệch với thực tế; đừng dựng lại ở FE.
   */
  public async getCustomerIdentityPropagationScope(
    existingCustomerId: number
  ): Promise<ICustomerIdentityPropagationScope> {
    const res = await http.get(
      "api/services/app/Approval/GetCustomerIdentityPropagationScope",
      { params: { id: existingCustomerId } }
    )
    return res.data.result
  }

  /** SKLCRMF01-290 — tiền kiểm quy trình duyệt; BE không ném lỗi khi thiếu cấu hình. */
  public async checkRequestConfiguration(params: {
    requestType: number
    projectId?: number | null
  }): Promise<IApprovalConfigurationAvailability> {
    // Tên `Check*` ⇒ ABP map sang POST. ABP KHÔNG coi enum là primitive ⇒ `requestType` bị bind [FromBody]
    // (gửi số trần làm JSON body, thiếu body ⇒ 415); `projectId` (long?) vẫn đi query string.
    const { requestType, projectId } = params
    const res = await http.post(
      "api/services/app/Approval/CheckRequestConfiguration",
      requestType,
      {
        params: { projectId },
        headers: { "Content-Type": "application/json" },
      }
    )
    return res.data.result
  }

  public async getListApprovalHistory(params): Promise<any> {
    const res = await http.get(
      "api/services/app/Approval/GetListApprovalHistory",
      { params }
    )
    return res.data.result
  }
}

export default new ApprovalService()
